import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { getApiUrl } from "@/utils/api";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { toast } from "@/utils/toast";

const emissionFactorSchema = z.object({
  name: z.string().min(1, "Name is required"),
  formula: z.string().min(1, "Formula is required"),
  coefficient: z.coerce
    .number()
    .min(0, "Coefficient must be a positive number"),
});

type EmissionFactorFormValues = z.infer<typeof emissionFactorSchema>;

interface EmissionFactorFormProps {
  id?: number;
  onSuccess?: () => void;
  onCancel?: () => void;
}

export const EmissionFactorForm = ({
  id,
  onSuccess,
  onCancel,
}: EmissionFactorFormProps) => {
  const { token } = useAuth();
  const queryClient = useQueryClient();
  const isEditMode = !!id;

  const form = useForm<EmissionFactorFormValues>({
    resolver: zodResolver(emissionFactorSchema),
    defaultValues: {
      name: "",
      formula: "",
      coefficient: 0,
    },
  });

  // Fetch emission factor data if in edit mode
  const { data: emissionFactorData, isLoading } = useQuery({
    queryKey: ["emission-factor", id],
    queryFn: async () => {
      if (!id) return null;
      const response = await fetch(getApiUrl(`emission-factors/${id}/`), {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        throw new Error("Failed to fetch emission factor");
      }
      const data = await response.json();
      return data;
    },
    enabled: isEditMode,
  });

  // Debug: Log when component renders and its state

  // Set form values when data is loaded
  useEffect(() => {
    if (emissionFactorData) {
      try {
        // Log the raw coefficient value and its type

        const coefficient =
          typeof emissionFactorData.coefficient === "string"
            ? parseFloat(emissionFactorData.coefficient)
            : Number(emissionFactorData.coefficient);

        if (isNaN(coefficient)) {
          throw new Error("Invalid coefficient value");
        }

        const formValues = {
          name: emissionFactorData.name || "",
          formula: emissionFactorData.formula || "",
          coefficient: coefficient || 0,
        };

        form.reset(formValues);
      } catch (error) {
        toast.error("Failed to load emission factor data");
      }
    }
  }, [emissionFactorData, form]);

  const mutation = useMutation({
    mutationFn: async (values: EmissionFactorFormValues) => {
      const url = id
        ? getApiUrl(`emission-factors/${id}/`)
        : getApiUrl("emission-factors/");
      const method = id ? "PATCH" : "POST";

      const response = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(values),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to save emission factor");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["emission-factors"] });
      onSuccess?.();
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const onSubmit = (values: EmissionFactorFormValues) => {
    mutation.mutate(values);
  };

  if (isLoading && isEditMode) {
    return <div>Loading...</div>;
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="flex flex-col h-full"
      >
        <div className="space-y-6 flex-1">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Name</FormLabel>
                <FormControl>
                  <Input
                    placeholder="Enter emission factor name"
                    {...field}
                    disabled={isEditMode} // Prevent editing name if in edit mode
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="formula"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Formula</FormLabel>
                <FormControl>
                  <Input placeholder="Enter formula" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="coefficient"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Coefficient</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    step="0.0001"
                    min="0"
                    placeholder="Enter coefficient value"
                    {...field}
                    onChange={(e) =>
                      field.onChange(parseFloat(e.target.value) || 0)
                    }
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="fixed inset-x-0 bottom-0 bg-background p-4 border-t flex justify-end space-x-2 mt-8">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={mutation.isPending}
          >
            Cancel
          </Button>
          <Button variant="default" type="submit" loading={mutation.isPending}>
            {isEditMode ? "Update" : "Create"}
          </Button>
        </div>
      </form>
    </Form>
  );
};
